import axios from 'axios'
import * as HttpStatus from 'http-status-codes'
import { flatten } from 'lodash'
import { useInfiniteQuery, useQuery } from 'react-query'
import { PaginatedResults } from 'shared/helpers/typeHelper'
import { parseQueryParams } from 'shared/helpers/utils'
import { UnemploymentGapBeforeReason } from 'shared/models/employment'

import { DocumentAttachmentType } from 'helpers/documents'

export enum EmploymentHistoryJobType {
  DRIVING = 'driving',
  NOT_DRIVING = 'not_driving',
}

export enum EmploymentHistoryEquipmentDriven {
  BUS = 'bus',
  CONVENTIONAL_TRACTOR = 'conventional_tractor',
  TRACTOR_TRAILER = 'tractor_trailer',
  STRAIGHT_TRUCK = 'straight_truck',
}

export enum EmploymentHistoryTrailerType {
  FLATBED = 'flatbed',
  REEFER = 'reefer',
  DRY_VAN = 'dry_van',
  OTHER = 'other',
}

export enum EmploymentHistorySource {
  DRIVER = 'driver',
  EMPLOYER = 'employer',
}

export enum EmploymentHistoryReasonLeaving {
  LAID_OFF = 'laid_off',
  TERMINATED = 'terminated',
  PERSONAL_REASONS = 'personal_reasons',
  CHANGED_JOBS = 'moved_companies',
  OTHER = 'other',
}

export enum EmploymentHistoryStatus {
  VERIFICATION_NOT_STARTED = 'verification_not_started',
  IN_PROGRESS = 'in_progress',
  VERIFIED_WITH_DISCREPANCY = 'verified_with_discrepancy',
  VERIFIED = 'verified',
  UNABLE_TO_VERIFY = 'unable_to_verify',
}

export const EmploymentHistoryStatusLabels = {
  [EmploymentHistoryStatus.VERIFICATION_NOT_STARTED]: 'Verification not started',
  [EmploymentHistoryStatus.IN_PROGRESS]: 'In progress',
  [EmploymentHistoryStatus.VERIFIED_WITH_DISCREPANCY]: 'Verified with discrepancy',
  [EmploymentHistoryStatus.VERIFIED]: 'Verified',
  [EmploymentHistoryStatus.UNABLE_TO_VERIFY]: 'Unable to verify',
}

export const employmentJobTypeOptions = [
  {
    name: 'Class 8 tractor/trailer driving job',
    id: EmploymentHistoryJobType.DRIVING,
  },
  {
    name: 'Non-Class 8 tractor/trailer driving job',
    id: EmploymentHistoryJobType.NOT_DRIVING,
  },
]

export const equipmentDrivenOptions = [
  {
    name: 'Bus',
    id: EmploymentHistoryEquipmentDriven.BUS,
  },
  {
    name: 'Conventional tractor',
    id: EmploymentHistoryEquipmentDriven.CONVENTIONAL_TRACTOR,
  },
  {
    name: 'Tractor-trailer',
    id: EmploymentHistoryEquipmentDriven.TRACTOR_TRAILER,
  },
  {
    name: 'Straight truck',
    id: EmploymentHistoryEquipmentDriven.STRAIGHT_TRUCK,
  },
]

export const trailerTypeOptions = [
  {
    name: 'Flatbed',
    id: EmploymentHistoryTrailerType.FLATBED,
  },
  {
    name: 'Reefer',
    id: EmploymentHistoryTrailerType.REEFER,
  },
  {
    name: 'Dry van',
    id: EmploymentHistoryTrailerType.DRY_VAN,
  },
  {
    name: 'Other',
    id: EmploymentHistoryTrailerType.OTHER,
  },
]

export const leavingReasonOptions = [
  {
    name: 'Laid off',
    id: EmploymentHistoryReasonLeaving.LAID_OFF,
  },
  {
    name: 'Terminated',
    id: EmploymentHistoryReasonLeaving.TERMINATED,
  },
  {
    name: 'Personal reasons',
    id: EmploymentHistoryReasonLeaving.PERSONAL_REASONS,
  },
  {
    name: 'Changed jobs',
    id: EmploymentHistoryReasonLeaving.CHANGED_JOBS,
  },
  {
    name: 'Other',
    id: EmploymentHistoryReasonLeaving.OTHER,
  },
]

export interface EmploymentHistory {
  id: string
  job_type: EmploymentHistoryJobType
  job_type_label: string
  status: EmploymentHistoryStatus
  status_label: string
  date_created: string
  date_updated: string
  can_edit: boolean
  is_complete: boolean
  missing_fields_to_complete: string[]
  driver_documents: Array<{
    url: string
    filename: string
  }>
  console_documents: Array<{
    url: string
    filename: string
  }>
  details: {
    start_date: string
    start_date_formatted: string
    end_date: string | null
    end_date_formatted: string | null
    company_address?: string
    dot_number?: string
    can_contact_employer: boolean
    is_own_authority?: boolean
    leaving_reason?: EmploymentHistoryReasonLeaving
    leaving_reason_detail?: string
    leaving_reason_label?: string
    contact_name?: string
    contact_email?: string
    contact_phone?: string
    subject_to_fmcsr_regulations?: boolean
    subject_to_drug_and_alcohol_testing?: boolean
    company_name: string
    company_dba?: string
    equipment_type?: EmploymentHistoryEquipmentDriven
    equipment_type_label?: string
    trailer_type?: EmploymentHistoryTrailerType
    trailer_type_label?: string
    source?: EmploymentHistorySource
    source_label?: string
    duration_in_months?: number
    verified_dates?: JobVerifiedDates[]
    position_held?: string
    unemployment_gap_before_reason: UnemploymentGapBeforeReason
  }
}

interface JobVerifiedDates {
  date_created: string
  date_updated: string
  end_date: string
  end_date_formatted: string
  id: string
  start_date: string
  start_date_formatted: string
  created_by: {
    email: string
    first_name: string
    id: string
    last_name: string
  }
}

export function fetchEmploymentHistory(
  userId: string,
  monthsBack?: number
): Promise<PaginatedResults<EmploymentHistory>> {
  const url = `/api/v1/console/employment-history/?user=${userId}${
    monthsBack ? `&months_back=${monthsBack}` : ''
  }`
  return axios.get(url).then((response) => {
    if (response.status === HttpStatus.OK) {
      return response.data
    }
  })
}

export function fetchEmploymentHistoryMonthsEmployed(
  userId: string,
  years: number,
  statuses?: EmploymentHistoryStatus[]
) {
  const url = `/api/v1/console/employment-history/months-employed/?user=${userId}`
  return axios.post(url, { years, statuses }).then((response) => {
    if (response.status === HttpStatus.OK) {
      return {
        monthsEmployed: response.data.months_employed,
        maxEndDate: response.data.max_end_date,
      }
    }
  })
}

export function createEmploymentHistory(
  userId: string,
  payload: Partial<Omit<EmploymentHistory, 'details'>> & { details: Partial<EmploymentHistory['details']> }
) {
  let url = `/api/v1/console/employment-history/?user=${userId}`
  return axios.post(url, payload).then((response) => {
    if (response.status === HttpStatus.OK) {
      return response.data
    }
  })
}

export function updateEmploymentHistory(
  id: string,
  userId: string,
  payload: Partial<Omit<EmploymentHistory, 'details'>> & { details: Partial<EmploymentHistory['details']> }
) {
  let url = `/api/v1/console/employment-history/${id}/?user=${userId}`
  return axios.patch(url, payload).then((response) => {
    if (response.status === HttpStatus.OK) {
      return response.data
    }
  })
}

export function uploadEmploymentJobDocument(id: string, userId: string, document: FormData) {
  const url = `/api/v1/console/employment-history/${id}/documents/?user=${userId}`
  const config = {
    headers: {
      'Content-Type': 'multipart/form-data',
    },
  }

  return axios.post(url, document, config).then((response) => {
    return response.data
  })
}

export function uploadEmploymentDocument(
  userId: string,
  document: FormData,
  attachmentType: DocumentAttachmentType
) {
  const url = `/api/v3/users/${userId}/employment-document/`
  document.append('attachment_type', attachmentType)

  const config = {
    headers: {
      'Content-Type': 'multipart/form-data',
    },
  }

  return axios.post(url, document, config).then((response) => {
    return response.data
  })
}

export function changeEmploymentJobStatus(id: string, userId: string, status: EmploymentHistoryStatus) {
  const url = `/api/v1/console/employment-history/${id}/status/?user=${userId}`

  return axios.post(url, { status }).then((response) => {
    return response.data
  })
}

interface EmploymentJobDetailVerificationDatesData {
  verified_dates: { start_date: string; end_date: string }[]
}

export function updateEmploymentJobDetailVerificationDates(
  id: string,
  userId: string,
  data: EmploymentJobDetailVerificationDatesData
) {
  const url = `/api/v1/console/employment-history/${id}/verified-dates/?user=${userId}`

  return axios.post(url, data).then((response) => {
    return response.data
  })
}

export function uploadJobDocument(id: string, userId: string, document: FormData) {
  const url = `/api/v1/console/employment-history/${id}/documents/?user=${userId}`
  const config = {
    headers: {
      'Content-Type': 'multipart/form-data',
    },
  }

  return axios.post(url, document, config).then((response) => {
    return response.data
  })
}

export function confirmEmployment(userId: string) {
  let url = `/api/v1/console/users/${userId}/confirm-employment/`
  return axios.post(url).then((response) => {
    return response.data
  })
}

export function useEmploymentHistory({
  userId,
  monthsBack,
  enabled = true,
}: {
  userId: string
  monthsBack?: number
  enabled?: boolean
}) {
  const { data, isFetching, refetch, isLoading } = useInfiniteQuery(
    `EMPLOYMENT_HISTORY-${userId}-${monthsBack}`,
    () => fetchEmploymentHistory(userId, monthsBack),
    {
      enabled,
      refetchOnWindowFocus: false,
      retry: true,
      getNextPageParam: (lastPage) => (lastPage.next ? parseQueryParams(lastPage.next).page : null),
    }
  )

  return {
    employmentHistory: flatten(data?.pages.map((page) => page.results)),
    refetch,
    isLoading,
    isFetching,
  }
}

export function useEmploymentHistoryMonthsEmployed({
  userId,
  years,
  statuses,
}: {
  userId: string
  years: number
  statuses?: EmploymentHistoryStatus[]
}) {
  const { data, isFetching, refetch, isLoading } = useQuery(
    `EMPLOYMENT_HISTORY_MONTHS_EMPLOYED-${userId}-${years}-${statuses?.join('')}`,
    () => fetchEmploymentHistoryMonthsEmployed(userId, years, statuses),
    {
      enabled: true,
      refetchOnWindowFocus: false,
      retry: true,
      retryDelay: (attempt) => Math.min(attempt > 1 ? 2 ** attempt * 1000 : 1000, 30 * 1000),
    }
  )

  return {
    monthsEmployed: data?.monthsEmployed,
    maxEndDate: data?.maxEndDate,
    refetch,
    isLoading,
    isFetching,
  }
}
